import { DraftSectionItem } from '../../components/Section/Section.model'
import {
  DraftConjointItem,
  DraftLogicClauseProposition,
  DraftMatrixRow,
  DraftTextHighlighterItem,
  EntrySettingValue,
  ForkTag,
  KpiDefinition,
  LoopSpecificDisplayLogic
} from '../gql-gen/questionnaire/graphql'
import { DraftEntryResponseOption } from '../gql-gen/questionnaire/graphql'
import { ResponseOptionType } from './shared'

export type EntryKpi = Pick<
  KpiDefinition,
  | 'kpiId'
  | 'metricType'
  | 'topN'
  | 'base'
  | 'rowId'
  | 'optionIds'
  | 'negativeOptionIds'
  | 'label'
>

export interface MediaEnabledEntry {
  questionMedia?: QuestionMedia
}

export interface DraftQuestionnaire {
  questionnaireId: string
  entries: DraftQuestionnaireEntry[]
  audienceEntries: DraftQuestionnaireEntry[]
  createdDate: string
  sinceDate: string
  validationErrors: (
    | MatrixValidationErrors
    | QuestionValidationErrors
    | ForkValidationErrors
  )[]
}

export interface QuestionValidationErrors {
  questionLk: string
  errors: QuestionValidationError[]
  responseOptionErrors: ResponseOptionsValidationErrors[]
}

export interface ForkValidationErrors {
  forkId: string
  errors: ForkValidationError[]
}

export enum ForkValidationError {
  ForkBranchesInvalidTotalError = 'ForkBranchesInvalidTotalError'
}

export interface ResponseOptionsValidationErrors {
  responseOptionLk: string
  errors: ResponseOptionValidationError[]
}

export enum ResponseOptionValidationError {
  EmptyResponseOptionValueError = 'EmptyResponseOptionValueError',
  DuplicateResponseOptionValueError = 'DuplicateResponseOptionValueError',
  InvalidRoute = 'InvalidRoute',
  InvalidDisplayLogic = 'InvalidDisplayLogic'
}

export enum QuestionValidationError {
  EmptyQuestionTextError = 'EmptyQuestionTextError',
  ResponseOptionCountLessThan2Error = 'ResponseOptionCountLessThan2Error',
  MatrixRowCountLessThan2Error = 'MatrixRowCountLessThan2Error',
  ResponseRangeMinCharactersExceedError = 'ResponseRangeMinCharactersExceedError',
  QuestionLogicInvalidQuestionError = 'QuestionLogicInvalidQuestionError',
  EmptyContentError = 'EmptyContentError',
  EmptyPackageLabelError = 'EmptyPackageLabelError',
  PackageMissingEligibleValueError = 'PackageMissingEligibleValueError',
  MissingAppearanceImageError = 'MissingAppearanceImageError'
}

export enum SliderValidationError {
  SliderInvalidRangeError = 'SliderInvalidRangeError',
  SliderInvalidStepError = 'SliderInvalidStepError'
}

export enum ConjointAttributeValidationError {
  EmptyConjointAttributeNameError = 'EmptyConjointAttributeNameError',
  DuplicateConjointAttributeNameError = 'DuplicateConjointAttributeNameError'
}

export enum ConjointOptionValidationError {
  EmptyConjointOptionValueError = 'EmptyConjointOptionValueError',
  DuplicateConjointOptionValueError = 'DuplicateConjointOptionValueError'
}

export interface ConjointAttributeValidationErrors {
  attributeId: string
  errors: ConjointAttributeValidationError[]
}

export interface ConjointOptionValidationErrors {
  attributeId: string
  optionId: string
  errors: ConjointOptionValidationError[]
}

export interface ValidationError {
  errors?: QuestionValidationError[]
  rowErrors?: MatrixRowValidationErrors[]
  responseOptionErrors?: ResponseOptionsValidationErrors[]
  sliderErrors?: SliderValidationError[]
  forkErrors?: ForkValidationError[]
  conjointAttributeErrors?: ConjointAttributeValidationErrors[]
  conjointOptionErrors?: ConjointOptionValidationErrors[]
}

export interface MatrixValidationErrors {
  matrixTitleLk: string
  errors: MatrixValidationError[]
  rowErrors: MatrixRowValidationErrors[]
  columnErrors: MatrixColumnValidationErrors[]
}

export interface MatrixColumnValidationErrors {
  responseOptionLk: string
  errors: MatrixColumnValidationError[]
}
export enum MatrixColumnValidationError {
  EmptyMatrixColumnError = 'EmptyMatrixColumnError',
  DuplicateMatrixColumnError = 'DuplicateMatrixColumnError'
}
export interface MatrixRowValidationErrors {
  questionLk: string
  errors: MatrixRowValidationError[]
}

export enum MatrixRowValidationError {
  EmptyMatrixRowError = 'EmptyMatrixRowError',
  DuplicateMatrixRowError = 'DuplicateMatrixRowError',
  InvalidDisplayLogic = 'InvalidDisplayLogic'
}

export enum MatrixValidationError {
  EmptyMatrixTitleError = 'EmptyMatrixTitleError',
  MatrixColumnCountLessThan2Error = 'MatrixColumnCountLessThan2Error',
  MatrixRowCountLessThan2Error = 'MatrixRowCountLessThan2Error'
}

type EntryTypes = {
  [EntryType.QuestionEntryType]: DraftQuestionItem
  [EntryType.TextCardEntryType]: DraftTextCardItem
  [EntryType.MatrixEntryType]: DraftMatrixItem
  [EntryType.ForkEntryType]: DraftForkItem
  [EntryType.SectionEntryType]: DraftSectionItem
  [EntryType.TextHighlighterEntryType]: DraftTextHighlighterItem
  [EntryType.ConjointEntryType]: DraftConjointItem
}

export type DraftQuestionnaireEntry<
  Type extends keyof EntryTypes = keyof EntryTypes
> = {
  id: string
  sectionId: string | undefined
  number: number
  position: number
  entryId: string
  questionKind: QuestionKind
  contextPosition: number
  forks: ForkTag[]
  createdDate: string
  sinceDate: string
  __typename: string
  loopSpecificDisplayLogic: LoopSpecificDisplayLogic | null
  // Survey ids of the markets where this entry is hidden. Empty/absent = visible in all markets.
  hiddenInMarkets?: string[] | null
  // Optional so hand-built entries still compile; the server always sends a list.
  kpis?: EntryKpi[] | null
  // Follows the entry from wave to wave; only content with one can be a KPI.
  trackingId?: string | null
} & {
  [K in keyof EntryTypes]: {
    entryType: K
    entryItem: EntryTypes[K]
  }
}[Type]

export enum QuestionKind {
  AudienceKind = 'AudienceKind',
  QuestionnaireKind = 'QuestionnaireKind'
}

export enum EntryType {
  QuestionEntryType = 'QuestionEntryType',
  TextCardEntryType = 'TextCardEntryType',
  MatrixEntryType = 'MatrixEntryType',
  ForkEntryType = 'ForkEntryType',
  SectionEntryType = 'SectionEntryType',
  TextHighlighterEntryType = 'TextHighlighterEntryType',
  ConjointEntryType = 'ConjointEntryType'
}

export enum QuestionTypeCode {
  Basic = 'basic',
  CustomAudience = 'custom_audience',
  StandardAudience = 'standard_audience',
  FreeText = 'free_text',
  Ranked = 'ranked',
  Matrix = 'matrix',
  Scale = 'scale',
  TextCard = 'text_card',
  Fork = 'fork',
  Section = 'section',
  MaxDiff = 'max_diff',
  TextHighlighter = 'text_highlighter',
  Conjoint = 'conjoint'
}

export enum EntryPrefix {
  QuestionnaireKind = 'Q',
  AudienceKind = 'A',
  TextCardEntryType = 'T',
  SectionEntryType = 'S'
}

export interface DraftForkItem {
  fork: Fork
  __typename: string
}

export interface Fork {
  forkId: string
  name: string
  branches: ForkBranch[]
  __typename: string
}

export interface ForkBranch {
  label: string
  branchNumber: number
  percentage: number
  __typename: string
}

// One country's own wording beside the shared per-language rows; its language
// plus the country is the locale (fr + CA is fr-CA).
export interface LocaleTranslation<T> {
  country: string
  translation: T
  __typename?: string
}

export interface QuestionTranslation {
  language: string
  text: string
  textStyling?: string | null
  __typename?: string
}

export interface ResponseOptionTranslation {
  language: string
  value: string
  __typename?: string
}

export interface DraftQuestionItem extends MediaEnabledEntry {
  questionTypeCode: QuestionTypeCode
  questionLk: string
  question?: Question
  questionScale?: QuestionScale
  questionLogic: DraftLogicClauseProposition[][]
  responseOptions: DraftEntryResponseOption[]
  maxDiffSpecification: MaxDiffSpecification | null
  maxDiffLabelTranslations?: MaxDiffLabelTranslation[] | null
  localeMaxDiffLabelTranslations?:
    | LocaleTranslation<MaxDiffLabelTranslation>[]
    | null
  settingValues: EntrySettingValue[]
  __typename: string
}

export interface MaxDiffSpecification {
  mostLabel: MaxDiffLocalisedLabel
  leastLabel: MaxDiffLocalisedLabel
  attributeLabel: MaxDiffLocalisedLabel
  experimentalDesigns: ExperimentalDesign[] | null
  __typename: string
}

// The most/least/attribute labels are one record per language, each field
// optional — the backend merges a partial input onto the existing record.
export interface MaxDiffLabelTranslation {
  language: string
  mostLabel?: string | null
  leastLabel?: string | null
  attributeLabel?: string | null
  __typename?: string
}

export interface MaxDiffLocalisedLabel {
  text: string
  language: string
  country: string
  __typename: string
}

export interface ExperimentalDesign {
  version: number
  sets: ResponseOptionSet[]
  __typename: string
}

export interface ResponseOptionSet {
  setNumber: number
  responseOptions: number[]
  __typename: string
}

export interface SliderNumbers {
  min: number
  max: number
  step: number
}

export interface ScaleRange extends SliderNumbers {
  startingPosition: number
  createdDate: string
  sinceDate: string
  __typename: string
}

export interface LocalisedScaleLabel {
  text: string
  language: string
  country: string
  createdDate: string
  sinceDate: string
}

export interface ScaleLabelTranslation {
  language: string
  country: string
  text: string
  __typename?: string
}

export interface QuestionScaleLabel {
  label: LocalisedScaleLabel
  labelTranslations?: ScaleLabelTranslation[]
  localeLabelTranslations?: LocaleTranslation<ScaleLabelTranslation>[] | null
  position: number
  __typename: string
}

export interface QuestionScale {
  range: ScaleRange
  labels: QuestionScaleLabel[]
  __typename: string
}

export interface QuestionMedia {
  mediaName: string
  mediaUrl: string
  mediaType: MediaType
  createdDate: string
  sinceDate: string
  renderedMediaUrl: string
  __typename: string
}

export interface ResponseOptionMedia {
  mediaName: string
  mediaUrl: string
  mediaType: MediaType
  renderedMediaUrl: string
  __typename: string
}

export enum MediaType {
  Image = 'Image',
  Video = 'Video'
}

export interface Question {
  questionId: string
  text: string
  textStyling?: string | null
  createdDate: string
  translations?: QuestionTranslation[] | null
  localeTranslations?: LocaleTranslation<QuestionTranslation>[] | null
  __typename: string
}

export interface ResponseOption {
  responseOptionId: string
  rotype: ResponseOptionType
  value: string
  createdDate: string
  translations?: ResponseOptionTranslation[] | null
  __typename: string
}

export interface Qualification {
  createdDate: string
  sinceDate: string
  __typename: string
}

export interface Route {
  sinceDate: string
  targetNumber: number
  __typename: string
}

export interface TextCardItem {
  textCard: TextCard
  __typename: string
}

export interface TextCard {
  textCardId: string
  title: string
  titleStyling: string | null
  subtitle: string
  body: string
  createdDate: string
  __typename: string
}

export interface DetachedEntryResponseOption {
  questionnaireId: string
  questionLk: string
  responseOptionLk: string
  detachedDate: string
  __typename: string
}

export enum QuestionSettingCode {
  HiddenInWave = 'hidden',
  BasicChoice = 'basic_choice',
  CharacterLimit = 'character_limit',
  ChoiceLimit = 'choice_limit',
  ChoiceIntervalMin = 'choice_interval_min',
  ChoiceIntervalMax = 'choice_interval_max',
  MatrixChoice = 'matrix_choice',
  DontKnow = 'default_option_dont_know',
  PreferNotToSay = 'default_option_skip',
  NoneOfThese = 'default_option_none',
  NotApplicable = 'default_option_not_applicable',
  RankChoiceRequisite = 'choice_requisite',
  RandomiseOptions = 'randomise_options',
  SectionRandomiseQuestions = 'section_questions_randomisation',
  Looping = 'looping',
  MatrixRandomiseRows = 'matrix_randomise_rows',
  MatrixRandomiseResponseOptions = 'matrix_randomise_options',
  Routing = 'routing',
  FlipOptions = 'flip_options',
  MatrixFlipOptions = 'matrix_flip_options',
  ContentStructure = 'content_structure',
  SliderLabels = 'slider_labels',
  CustomAudienceChoice = 'custom_audience_choice',
  StandardAudienceChoice = 'standard_audience_choice',
  QuestionTimer = 'question_timer',
  AutoSumTarget = 'auto_sum_target',
  SectionsRandomisation = 'sections_randomisation',
  CharacterMinimum = 'character_minimum',
  MatrixMobileViewOnly = 'matrix_mobile_view_only',
  AutoAnswerAll = 'auto_answer_all',
  DisableSourceMedia = 'disable_source_media',
  HideTracker = 'hide_slider_tracker',
  HideSelectedSliderValue = 'hide_selected_slider_value',
  SliderInitialPlacement = 'slider_initial_placement',
  TextHighlighterComments = 'text_highlighter_comments',
  ConjointPanelSize = 'conjoint_panel_size',
  ConjointNumberOfPanels = 'conjoint_number_of_panels',
  ConjointPackageLabelsEnabled = 'conjoint_package_labels_enabled',
  ConjointPackageLabels = 'conjoint_package_labels'
}

export enum SettingValue {
  SingleChoice = 'single_choice',
  MultipleChoice = 'multiple_choice',
  OneHundred = '100',
  TwoHundred = '200',
  EightHundred = '800',
  Enabled = 'enabled',
  Disabled = 'disabled',
  Numeric1 = 'numeric__^-?[0-9]\\d*(\\.\\d+)?$',
  Numeric2 = 'numeric_posint__^\\d+$',
  Numeric3 = 'numeric_int__^-?\\d+$',
  PostCode = 'postcode_uk__^[a-zA-Z]{1,2}\\d[a-zA-Z\\d]?\\s*\\d[a-zA-Z]{2}$',
  Alphanumeric = 'alphanumeric__^[A-Za-z0-9\\s]+$',
  Email = 'email__^[^@\\s]+@[^@\\s]+\\.[^@\\s]+$',
  Unstructured = 'unstructured',
  PostCodeFull = 'postcode_uk__full',
  Two = '2',
  ThreeHundred = '300',
  FiveHundred = '500',
  Category = 'category',
  Left = 'left',
  Middle = 'middle',
  Right = 'right'
}

export interface SettingInputInput {
  questionSettingCode: QuestionSettingCode
  settingValue: SettingValue | string
}

export interface TextCardTranslation {
  language: string
  title: string
  subtitle: string
  body: string
  titleStyling?: string | null
  __typename?: string
}

export interface MatrixTitleTranslation {
  language: string
  title: string
  titleStyling?: string | null
  __typename?: string
}

export interface DraftTextCardItem extends MediaEnabledEntry {
  textCardLk: string
  textCard: TextCard
  questionLogic: DraftLogicClauseProposition[][]
  settingValues: EntrySettingValue[]
  translations?: TextCardTranslation[] | null
  localeTranslations?: LocaleTranslation<TextCardTranslation>[] | null
  __typename: string
}

export type DraftMatrixItem = {
  matrixTitleLk: string
  matrixTitle?: MatrixTitle
  matrixRows: DraftMatrixRow[]
  questionLogic: DraftLogicClauseProposition[][]
  responseOptions: DraftEntryResponseOption[]
  settingValues: EntrySettingValue[]
  matrixQualification: null | MatrixQualification
  translations?: MatrixTitleTranslation[] | null
  localeTranslations?: LocaleTranslation<MatrixTitleTranslation>[] | null
  __typename: string
}

export interface MatrixTitle {
  matrixTitleId: string
  title: string
  titleStyling: string | null
  __typename: string
}

export type MatrixQualification = {
  qualifyingOptions: string[]
  matrixRowQualifyingCondition: MatrixRowQualifyingCondition
  __typename: string
}

export type MatrixRowQualifyingCondition = {
  numberOfQualifyingRows: number
  operator: 'GreaterThanOrEquals' | 'LessThanOrEquals'
  __typename: string
}

export interface DetachedEntryMatrixRow {
  questionnaireId: string
  matrixTitleLk: string
  questionLk: string
  detachedDate: string
  entry: DraftQuestionnaireEntry<EntryType.MatrixEntryType>
  __typename: string
}

export interface LocalisedQuestion {
  __typename: string
  questionId: string
  questionTypeCode: string
  language: string
  country: string
  text: string
  createdDate: string
  localisedDate: string
}

export interface DetachedEntryMatrixResponseOption {
  questionnaireId: string
  matrixTitleLk: string
  responseOptionLk: string
  detachedDate: string
  __typename: string
}

export interface DraftRoute {
  questionnaireId: string
  questionLk: string
  responseOptionLk: string
  targetNumber: number
  __typename: string
}

export interface PositionTextSelection {
  startPositionSelectedText?: number | null
  endPositionSelectedText?: number | null
}
